import { JobStatusEnum, jobStatusToLabel } from "@/lib/types/job";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { InvoiceType } from "@/dentlab/src/types/database-enums/invoices-enums";
import { Member } from "@/dentlab/src/store/UserManagement/user-management.store";
import { getProfileDisplayName } from "@/lib/utils/client-utils";

const technicianIdToLabel = (
	technicianId: string,
	membersLookup: Record<string, Member>
) => {
	const technician = membersLookup[technicianId];
	if (!technician) return "";
	return getProfileDisplayName(technician);
};

const jobItemTypeToLabel = {
	[JobItemTypeEnum.ARTICLE_MATERIAL]: "Material",
	[JobItemTypeEnum.ARTICLE_POSTAGE]: "Porto",
	[JobItemTypeEnum.ARTICLE_EXTERNAL_WORK]: "Fremdarbeit",
	[JobItemTypeEnum.ARTICLE_DISCOUNTS]: "Rückvergütung/Barrabatt",
	[JobItemTypeEnum.ARTICLE_FIXED_RATE]: "Arbeitspauschale",
	[JobItemTypeEnum.TARIFF]: "Arbeit",
};

const invoiceTypeToLabel = {
	[InvoiceType.BARRECHNUNG]: "Barrechnung",
	[InvoiceType.KARTENZAHLUNGSRECHNUNG]: "Kartenzahlungsquittung",
	[InvoiceType.AKONTORECHNUNG]: "Akontorechnung",
	[InvoiceType.MONATSRG]: "Monatsrechnung",
	[InvoiceType.RECHNUNG]: "Rechnung",
	[InvoiceType.PSEUDORG]: "Pseudorechnung",
	[InvoiceType.RUECKVERGUETUNG]: "Rückvergütung",
	[InvoiceType.MAHNGEBUEHR]: "Mahngebühr",
};

const formatNumericValue = (value: any): string => {
	if (value === null || value === undefined || value === "") return "";

	const num = Number(value);
	if (!isNaN(num)) {
		return num.toLocaleString("de-DE", {
			minimumFractionDigits: 1,
			maximumFractionDigits: 2,
		});
	}
	return value.toString();
};

export const getLieferscheinRow = (
	item: any,
	membersLookup: Record<string, Member>
) => {
	return {
		"Auftrag erstellt am": item.job_created_at
			? new Date(item.job_created_at).toLocaleString("de-DE", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
					second: "2-digit",
				})
			: "",
		Auftragstitel: item.job_title || "",
		Auftragscode: item.job_code || "",
		Auftragsbeschreibung: item.job_description || "",
		"Zahnfarbe des Auftrags": item.job_tooth_color || "",
		Auftragsstatus:
			jobStatusToLabel[item.job_status as JobStatusEnum] || "Unbekannt",
		"Auftragseingang am": item.job_incoming_order_date
			? new Date(item.job_incoming_order_date).toLocaleDateString(
					"de-DE",
					{
						year: "numeric",
						month: "2-digit",
						day: "2-digit",
						hour: "2-digit",
						minute: "2-digit",
						second: "2-digit",
					}
				)
			: "",
		"Auftragsausgang am": item.job_outgoing_order_date
			? new Date(item.job_outgoing_order_date).toLocaleDateString(
					"de-DE",
					{
						year: "numeric",
						month: "2-digit",
						day: "2-digit",
						hour: "2-digit",
						minute: "2-digit",
						second: "2-digit",
					}
				)
			: "",
		"TP-Variation des Auftrags": item.job_tp_variation || "",
		Währung: item.job_currency || "",
		"Lieferschein erstellt am": item.job_document_created_at
			? new Date(item.job_document_created_at).toLocaleString("de-DE", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
					second: "2-digit",
				})
			: "",
		"Titel des Lieferscheins": item.job_document_title || "",
		"Status des Lieferscheins": item.job_document_status
			? jobStatusToLabel[item.job_document_status as JobStatusEnum]
			: "",
		"Betrag des Lieferscheins": item.job_document_amount
			? Number(item.job_document_amount).toLocaleString("de-DE", {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})
			: "",
		"Rabatt auf Arbeit (Lieferschein)": formatNumericValue(
			item.job_document_discount_work
		),
		"Rabatt auf Material (Lieferschein)": formatNumericValue(
			item.job_document_discount_material
		),
		"Auftragsposten erstellt am": item.job_item_created_at
			? new Date(item.job_item_created_at).toLocaleString("de-DE", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
					second: "2-digit",
				})
			: "",
		"Eingabecode des Auftragspostens": `"${item.job_item_code_e}"` || "",
		"Code des Auftragspostens": `"${item.job_item_code}"` || "",
		"Menge des Auftragspostens": item.job_item_quantity || "",
		"Beschreibung des Auftragspostens": item.job_item_description || "",
		"Notizen zum Auftragsposten": item.job_item_note || "",
		"Preis des Auftragspostens": item.job_item_price
			? Number(item.job_item_price).toLocaleString("de-DE", {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})
			: "",
		"Garantie des Auftragspostens": item.job_item_is_warranty
			? "Ja"
			: "Nein",
		"Steuersatz des Auftragspostens": formatNumericValue(
			item.job_item_tax_rate
		), //TODO: Should be from job_document
		"TP-Wert des Auftragspostens": formatNumericValue(
			item.job_item_tp_value
		),
		"Rabatt des Auftragspostens": formatNumericValue(
			item.job_item_discount
		),
		"Lotnummer des Auftragspostens": item.job_item_lot || "",
		"Hersteller des Auftragspostens": item.job_item_manufacturer || "",
		"Zusammensetzung des Auftragspostens": item.job_item_composition || "",
		"CE-Nummer des Auftragspostens": item.job_item_ce_number || "",
		"Typ des Auftragspostens":
			jobItemTypeToLabel[item.job_item_type as JobItemTypeEnum] || "",
		"Cluster des Auftragspostens": item.job_item_cluster || "",
		"Techniker 1":
			technicianIdToLabel(item.job_item_profile1_id, membersLookup) || "",
		"Anteil des Technikers 1": formatNumericValue(
			item.job_item_techn_perc1
		),
		"Techniker 2":
			technicianIdToLabel(item.job_item_profile2_id, membersLookup) || "",
		"Anteil des Technikers 2": formatNumericValue(
			item.job_item_techn_perc2
		),
		"Techniker 3":
			technicianIdToLabel(item.job_item_profile3_id, membersLookup) || "",
		"Anteil des Technikers 3": formatNumericValue(
			item.job_item_techn_perc3
		),
		"Anrede des Patienten": item.patient_title || "",
		"Vorname des Patienten": item.patient_first_name || "",
		"Nachname des Patienten": item.patient_last_name || "",
		"Code des Patienten": item.patient_code || "",
		"Geburtsdatum des Patienten": item.patient_birth_date || "",
		"Geschlecht des Patienten": item.patient_gender || "",
		"Geburtstag des Patienten": item.patient_birthday || "",
		Garantor: item.job_guarantor_id ? "Ja" : "Nein",
		"Anrede des Auftraggebers": item.client_title || "",
		"Vorname des Auftraggebers": item.client_first_name || "",
		"Nachname des Auftraggebers": item.client_last_name || "",
		"Email des Auftraggebers": item.client_email || "",
		"Telefon des Auftraggebers":
			item.client_phone_personal || item.client_phone_business || "",
	};
};

export const getRechnungRow = (item: any) => {
	return {
		"Rechnung erstellt am": item.invoice_created_at
			? new Date(item.invoice_created_at).toLocaleString("de-DE", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
					second: "2-digit",
				})
			: "",
		Rechnungsdatum: item.invoice_date
			? new Date(item.invoice_date).toLocaleString("de-DE", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
					second: "2-digit",
				})
			: "",
		Rechnungsnummer: item.invoice_doc_number || "",
		"Betrag der Rechnung": formatNumericValue(item.invoice_value) || "",
		Rechnungswährung: item.invoice_currency || "",
		"Art der Rechnung":
			invoiceTypeToLabel[item.invoice_type as InvoiceType] || "Unbekannt",
		"Betrag des Lieferscheins":
			formatNumericValue(item.job_document_amount) || "",
		Auftragstitel: item.job_title || "",
		Auftragscode: item.job_code || "",
		"Rabatt auf Arbeit (Lieferschein)":
			formatNumericValue(item.invoice_discount_work) || "0",
		"Rabatt auf Material (Lieferschein)":
			formatNumericValue(item.invoice_discount_material) || "0",
		"Anrede des Auftraggebers": item.client_title || "",
		"Vorname des Auftraggebers": item.client_first_name || "",
		"Nachname des Auftraggebers": item.client_last_name || "",
	};
};
